import { useEffect, useRef, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { Block, BlockStatus, MeResponse, Pillar } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { useRouter } from "../../router/Router";
import { BlockCard, statusLabel } from "./BlockCard";
import { type BlockSortKey, sortBlocks } from "./blockSort";
import { BlockSortSelect } from "./BlockSortSelect";
import { CreateBlockForm } from "./CreateBlockForm";
import { creatableLevels } from "./createBlockOptions";
import { fetchAllBlocks } from "./fetchAllBlocks";
import { projectPillarId } from "./pillarFilter";
import { SeasonPicker } from "./SeasonPicker";
import { useBlockSeason } from "./useBlockSeason";

interface State {
  status: "loading" | "ready" | "error";
  blocks: Block[];
  pillars: Pillar[];
  errorMessage: string | null;
}

const initial: State = { status: "loading", blocks: [], pillars: [], errorMessage: null };

const statusOptions: BlockStatus[] = ["assigned", "in-progress", "pending-approval", "complete"];

// All Blocks is the flat, filterable list across every level, largest first by
// default (Appendix D). Pillar and status filter client-side; owner/team filters arrive
// with assignment in a later phase. The season defaults to the active one;
// leadership can switch it via the SeasonPicker (hidden for everyone else).
export function AllBlocksPage({ me }: { me: MeResponse }) {
  const router = useRouter();
  const [state, setState] = useState<State>(initial);
  const [pillarId, setPillarId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const { seasonId, season, seasons, setSeasonId, ready } = useBlockSeason(me);
  const [sort, setSort] = useState<BlockSortKey>("size");
  const [creating, setCreating] = useState(false);
  // A closed season's totals are frozen and the server rejects every create.
  const canCreate = creatableLevels(me).length > 0 && season?.status !== "closed";
  // Mass upload is leadership-only: it creates Projects (create_top) and may
  // target a draft season, which only seasons:manage can pick.
  const canMassUpload = hasPermission(me, "blocks:create_top") && hasPermission(me, "blocks:seasons:manage");

  // Rapid season switches (and a refresh after a create) overlap, and only the
  // latest may land: an older season's blocks would feed the list and the
  // create form's parent picker.
  const latestRefresh = useRef(0);

  useEffect(() => {
    if (ready) {
      void refresh(seasonId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, ready]);

  async function refresh(season: string) {
    const request = ++latestRefresh.current;
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));
    try {
      const [blocks, pillars] = await Promise.all([fetchAllBlocks(season), apiClient.listPillars()]);
      if (request === latestRefresh.current) {
        setState({ status: "ready", blocks, pillars, errorMessage: null });
      }
    } catch (caught) {
      if (request === latestRefresh.current) {
        setState({ status: "error", blocks: [], pillars: [], errorMessage: messageFor(caught) });
      }
    }
  }

  // Archived pillars stay in the dropdown only while a visible block still
  // references them.
  const referencedPillarIds = new Set(
    state.blocks.map((block) => block.pillar_id).filter((id): id is string => id !== null)
  );
  const pillarOptions = state.pillars.filter(
    (pillar) => !pillar.archived || referencedPillarIds.has(pillar.id)
  );

  const blocksById = new Map(state.blocks.map((block) => [block.id, block]));
  const visible = sortBlocks(
    state.blocks
      .filter((block) => (pillarId === "" ? true : projectPillarId(block, blocksById) === pillarId))
      .filter((block) => (status === "" ? true : block.status === status)),
    sort
  );

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>All Blocks</h2>
        {(canCreate || canMassUpload) && (
          <div className="form-row form-row--inline">
            {canCreate && (
              <button type="button" className="btn btn--ghost" onClick={() => setCreating((open) => !open)}>
                {creating ? "Cancel" : "New block"}
              </button>
            )}
            {canMassUpload && (
              <button type="button" className="btn btn--ghost" onClick={() => router.navigate("/blocks/import")}>
                Mass upload
              </button>
            )}
          </div>
        )}
        <div className="form-row form-row--inline">
          <SeasonPicker seasons={seasons} value={seasonId} onChange={setSeasonId} />
          <label>
            Pillar
            <select value={pillarId} onChange={(event) => setPillarId(event.target.value)}>
              <option value="">All pillars</option>
              {pillarOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {statusLabel(option)}
                </option>
              ))}
            </select>
          </label>
          <BlockSortSelect value={sort} onChange={setSort} />
        </div>

        {creating && canCreate && (
          <CreateBlockForm
            me={me}
            seasonId={seasonId}
            blocks={state.status === "ready" ? state.blocks : []}
            pillars={state.pillars}
            onCreated={() => void refresh(seasonId)}
          />
        )}

        {state.status === "loading" && <p className="loading">Loading…</p>}
        {state.status === "error" && state.errorMessage !== null && (
          <p className="form-row__error">{state.errorMessage}</p>
        )}
        {state.status === "ready" && visible.length === 0 && (
          <p className="muted">{emptyMessage(state.blocks.length, seasonId)}</p>
        )}
        {state.status === "ready" && visible.length > 0 && (
          <div className="block-card-grid">
            {visible.map((block) => (
              <BlockCard key={block.id} block={block} onOpen={(id) => router.navigate(`/blocks/${id}`)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// emptyMessage distinguishes three empty states: filtered-out (blocks exist but
// none match), a deliberately-selected empty season (leadership), and the
// default view when no season is active at all.
function emptyMessage(loadedCount: number, seasonId: string): string {
  if (loadedCount > 0) {
    return "No blocks match.";
  }
  if (seasonId !== "") {
    return "This season has no blocks yet.";
  }
  return "No active blocks season yet — nothing to show.";
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
